import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Loader2, CreditCard, Building2, ShieldCheck, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * SavedPaymentMethodsList (iter241 · iter369 interactive)
 *
 * Admin/staff view + management of a parent or customer's saved Stripe
 * payment methods. Pulls live from
 * `/api/stripe/admin/payment-methods/{userId}`.
 *
 * iter369 — single-primary model with safeguards:
 *   • "Make primary" sets the one default method.
 *   • "Remove" detaches a method; the primary can't be removed while
 *     other methods exist (swap first), and the LAST method can only be
 *     removed for an inactive parent/customer (backend-enforced).
 *
 * Props:
 *   userId      — the parent/customer id whose PMs to show/manage
 *   compact     — inline-style (1 line per PM); default false
 *   onCount     — optional callback fired with the PM count after load
 *   interactive — show Make-primary / Remove actions (default true)
 *   onChanged   — optional callback fired after a successful change
 */
export default function SavedPaymentMethodsList({
  userId,
  compact = false,
  onCount,
  interactive = true,
  onChanged,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmPm, setConfirmPm] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/stripe/admin/payment-methods/${userId}`);
      setData(data);
      onCount?.(data?.payment_methods?.length || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load saved payment methods');
      onCount?.(0);
    } finally {
      setLoading(false);
    }
  }, [userId, onCount]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/stripe/admin/payment-methods/${userId}`);
        if (cancelled) return;
        setData(data);
        onCount?.(data?.payment_methods?.length || 0);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Failed to load saved payment methods');
        onCount?.(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, onCount]);

  const handleSyncFromStripe = async () => {
    setSyncing(true);
    try {
      await api.post(`/stripe/admin/sync-stripe-customer-ids?user_id=${userId}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Sync from Stripe failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleMakePrimary = async (pmId) => {
    setBusyId(pmId);
    try {
      await api.put(`/stripe/admin/payment-methods/${userId}/${pmId}/primary`);
      toast.success('Primary payment method updated');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set primary method');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (pmId) => {
    setBusyId(pmId);
    try {
      // force=true lets the backend apply the inactive-account override for
      // the last method; for any non-last removal it is simply ignored.
      await api.delete(`/stripe/admin/payment-methods/${userId}/${pmId}?force=true`);
      toast.success('Payment method removed');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove payment method');
    } finally {
      setBusyId(null);
      setConfirmPm(null);
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-2 py-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading saved payment methods…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
        {error}
      </div>
    );
  }

  if (!data?.has_stripe_customer) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-slate-500 italic">
          No Stripe customer profile yet — the user hasn't saved any payment method.
        </div>
        <button
          onClick={handleSyncFromStripe}
          disabled={syncing}
          className="text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
          data-testid="sync-stripe-from-admin"
        >
          {syncing ? 'Searching Stripe…' : 'Search Stripe by email & link if found'}
        </button>
      </div>
    );
  }

  const pms = data.payment_methods || [];
  if (pms.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-slate-500 italic">
          Stripe customer exists but no payment methods on file.
        </div>
        <button
          onClick={handleSyncFromStripe}
          disabled={syncing}
          className="text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
          data-testid="resync-stripe-from-admin"
        >
          {syncing ? 'Re-syncing from Stripe…' : 'Re-sync from Stripe'}
        </button>
      </div>
    );
  }

  const isLast = pms.length === 1;

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'} data-testid="saved-pm-list">
      {pms.map((pm) => {
        const isCard = pm.type === 'card';
        const expSoon =
          isCard &&
          pm.exp_year && pm.exp_month &&
          new Date(pm.exp_year, pm.exp_month - 1) <
            new Date(new Date().setMonth(new Date().getMonth() + 2));
        const busy = busyId === pm.id;
        // The primary can't be removed while others remain — swap first.
        const removeBlockedAsPrimary = pm.is_default && !isLast;
        return (
          <div
            key={pm.id}
            className={`flex items-center gap-2 ${
              compact
                ? 'text-xs'
                : 'p-2 rounded-md border bg-slate-50 text-sm'
            }`}
            data-testid={`saved-pm-${pm.id}`}
          >
            {isCard ? (
              <CreditCard className="h-4 w-4 text-indigo-600 shrink-0" />
            ) : (
              <Building2 className="h-4 w-4 text-emerald-600 shrink-0" />
            )}
            <span className="font-medium capitalize">
              {isCard ? pm.brand : (pm.bank_name || 'Bank Account')}
            </span>
            <span className="text-slate-500">•••• {pm.last4}</span>
            {isCard && pm.exp_month && pm.exp_year && (
              <span className={`text-[11px] ${expSoon ? 'text-rose-700 font-medium' : 'text-slate-400'}`}>
                exp {String(pm.exp_month).padStart(2, '0')}/{String(pm.exp_year).slice(-2)}
                {expSoon ? ' · expiring soon' : ''}
              </span>
            )}
            {!isCard && pm.account_type && (
              <span className="text-[11px] text-slate-400 capitalize">{pm.account_type}</span>
            )}
            {pm.is_default && (
              <Badge variant="outline" className="ml-auto text-emerald-700 border-emerald-300 bg-emerald-50 text-[10px]" data-testid={`saved-pm-${pm.id}-primary-badge`}>
                <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                Primary
              </Badge>
            )}
            {interactive && (
              <div className={`flex items-center gap-1 ${pm.is_default ? '' : 'ml-auto'}`}>
                {!pm.is_default && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5 text-[11px] text-indigo-700 hover:bg-indigo-100"
                    disabled={busy}
                    onClick={() => handleMakePrimary(pm.id)}
                    data-testid={`saved-pm-${pm.id}-make-primary`}
                    title="Make this the primary method"
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Star className="h-3 w-3 mr-0.5" />}
                    Make primary
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1.5 text-[11px] text-rose-700 hover:bg-rose-100 disabled:opacity-40"
                  disabled={busy || removeBlockedAsPrimary}
                  onClick={() => setConfirmPm(pm)}
                  data-testid={`saved-pm-${pm.id}-remove`}
                  title={
                    removeBlockedAsPrimary
                      ? 'This is the primary method — set another as primary first.'
                      : 'Remove this payment method'
                  }
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        );
      })}

      <AlertDialog open={!!confirmPm} onOpenChange={(o) => { if (!o) setConfirmPm(null); }}>
        <AlertDialogContent data-testid="remove-pm-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove payment method?</AlertDialogTitle>
            <AlertDialogDescription>
              {isLast ? (
                <>
                  This is the <strong>only</strong> method on file. Removing it leaves the
                  parent/customer with no payment method — this is only permitted for an
                  <strong> inactive</strong> account. For an active account, add a replacement first.
                </>
              ) : (
                <>
                  This will detach {confirmPm?.type === 'card' ? (confirmPm?.brand || 'card') : 'the bank account'}
                  {' '}•••• {confirmPm?.last4} from the customer. They can add it again later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-pm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => confirmPm && handleRemove(confirmPm.id)}
              data-testid="remove-pm-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
